/**
 * IMPORTS
 */

import React, { useCallback, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { Pencil, Plus, Trash2, Undo2, UserX } from "lucide-react";

// pages - admin - gerenciar-usuarios-cliente - components
import { ModalCriarUsuarioCliente } from "@pages/admin/gerenciar-usuarios-cliente/components/modal-criar-usuario-do-cliente";
import { ModalEditarUsuarioDoCliente } from "@pages/admin/gerenciar-usuarios-cliente/components/modal-editar-usuario-do-cliente";
import { ModalExcluirUsuarioDoCliente } from "@pages/admin/gerenciar-usuarios-cliente/components/modal-excluir-usuario-do-cliente";
import { ModalInativarUsuarioDoCliente } from "@pages/admin/gerenciar-usuarios-cliente/components/modal-inativar-usuario-do-cliente";
import { SearchInputUsuariosCliente } from "@pages/admin/gerenciar-usuarios-cliente/components/search-usuarios-cliente";
import { dataTitleUsuariosCliente } from "@pages/admin/gerenciar-usuarios-cliente/helpers";

// hooks - react-query
import { useUsuariosCliente } from "@/hooks/usuários/buscar-usuarios-do-cliente";

// components
import { Button } from "@/presentation/components/button/button";
import { DynamicTable } from "@/presentation/components/table-custom/table";
import { Tabs, TabsList, TabsTrigger } from "@/presentation/components/tabs";

// store-zustand
import { useStoreZustandUsuarioCliente } from "@/store-zustand/usuarios-do-clientes";

// typings
import { type IUsuarioCliente } from "@/store-zustand/usuarios-do-clientes/index.d";

import { FEATURES, useFeature } from "@/utils/auth/features";

/**
 * Página de gerenciamento de usuários do cliente
 */
const ManageClientUsers = (): React.ReactElement => {
  const navigate = useNavigate();
  const podeCriarUsuario = useFeature(FEATURES.CRIAR_USUARIO_CLIENTE);
  const podeInativarUsuario = useFeature(FEATURES.INATIVAR_USUARIO_CLIENTE);
  const podeEditarUsuario = useFeature(FEATURES.EDITAR_USUARIOS_CLIENTE);

  const location = useLocation();
  const { cliente } = location.state || {};

  const user_cliente_id = cliente?.id;
  const {
    usuariosCliente,
    isLoading: isLoadingPage,
    paginations,
  } = useUsuariosCliente(user_cliente_id);
  const {
    currentPage,
    itemsPerPage,
    lastPage,
    setCurrentPage,
    totalItemsPage,
    isModalInativarUsuarioDoCliente,
    setisModalInativarUsuarioDoCliente,
    setUsuarioClienteSelecionado,
    isModalCadastrarUsuarioDoCliente,
    setIsModalCadastrarUsuarioDoCliente,
    isModalExcluirUsuarioDoCliente,
    setIsModalExcluirUsuarioDoCliente,
    isModalEditarUsuarioDoCliente,
    setIsModalEditarUsuarioDoCliente,
  } = useStoreZustandUsuarioCliente();

  const [userData, setUserData] = useState({
    fullName: cliente?.nome || "N/A",
    email: cliente?.email || "N/A",
    password: import.meta.env.VITE_PASSWORD_DEFAULT_FOR_USER_CLIENTE || "123456",
    confirmPassword: import.meta.env.VITE_PASSWORD_DEFAULT_FOR_USER_CLIENTE || "123456",
    changePassword: false,
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUserData({ ...userData, [e.target.name]: e.target.value });
  };

  /**função que vai para a página anterior ou próxima */
  const onGoToPage = useCallback(
    (action: number | "prev" | "next") => {
      const newPage =
        action === "prev"
          ? Math.max(1, currentPage - 1)
          : action === "next"
            ? currentPage + 1
            : action;

      setCurrentPage(newPage);
    },
    [currentPage, setCurrentPage]
  );

  /**função que volta para tela anterior */
  const handleGoBack = () => {
    // volta uma página no histórico de navegação
    navigate(-1);
  };

  return (
    <Tabs defaultValue="all">
      <div className="flex items-center">
        <TabsList>
          <TabsTrigger value="all">Gerenciar usuários</TabsTrigger>
        </TabsList>
        <div className="mb-4 ml-auto mr-4 flex items-center gap-2">
          <Button
            size="sm"
            variant="default"
            className="h-8 gap-1 text-neutral-50"
            onClick={handleGoBack}
          >
            <Undo2 className="h-3.5 w-3.5" />
            <span className="sr-only text-neutral-50 sm:not-sr-only sm:whitespace-nowrap">
              Voltar
            </span>
          </Button>

          {podeCriarUsuario && (
            <Button
              size="sm"
              variant="default"
              className="h-8 gap-1 text-neutral-50"
              onClick={() => setIsModalCadastrarUsuarioDoCliente(!isModalCadastrarUsuarioDoCliente)}
            >
              <Plus className="h-3.5 w-3.5" />
              <span className="sr-only text-neutral-50 sm:not-sr-only sm:whitespace-nowrap">
                Adicionar usuário
              </span>
            </Button>
          )}
        </div>
      </div>

      <div className="mx-auto w-full rounded-lg bg-white p-6 shadow-md dark:bg-white">
        <h2 className="dark:text-gray text-xl font-semibold text-gray-800">
          Gerenciar usuários do cliente
        </h2>
        <p className="mb-6 text-sm text-gray-500 dark:text-gray-400">
          Atualize informações pessoais do cliente.
        </p>

        {/* Grid Responsivo */}
        <div className="mb-4 grid grid-cols-1 gap-6 md:grid-cols-2">
          {/* Nome Completo */}
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-700">
              Nome completo
            </label>
            <input
              disabled
              type="text"
              name="fullName"
              value={userData.fullName}
              onChange={handleChange}
              className="w-full cursor-not-allowed rounded-lg border border-zinc-800 px-4 py-2 dark:text-gray-800"
              placeholder="Digite seu nome completo"
            />
          </div>

          {/* E-mail */}
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-700">
              E-mail (Importante para recuperação de senha)
            </label>
            <input
              disabled
              type="email"
              name="email"
              value={userData.email}
              onChange={handleChange}
              className="w-full cursor-not-allowed rounded-lg border border-zinc-800 px-4 py-2 dark:text-gray-800"
              placeholder="Digite seu e-mail"
            />
          </div>
        </div>

        <DynamicTable
          title="Usuários"
          description=""
          isLoadingPage={isLoadingPage}
          data={usuariosCliente || []}
          offset={1}
          totalItems={totalItemsPage}
          itemsPerPage={itemsPerPage}
          onGoToPage={onGoToPage}
          columns={dataTitleUsuariosCliente}
          paginations={paginations}
          currentPage={currentPage}
          lastPage={lastPage}
          childerSearch={<SearchInputUsuariosCliente />}
          actions={[
            {
              icon: Pencil,
              label: "Editar usuário",
              textColor: "text-zinc-600",
              visible: podeEditarUsuario,
              onClick: (item: IUsuarioCliente) => {
                setUsuarioClienteSelecionado(item);
                setIsModalEditarUsuarioDoCliente(true);
              },
            },
            {
              icon: UserX,
              label: "Usuários",
              textColor: "text-red-600",
              visible: podeInativarUsuario,
              onClick: (item: IUsuarioCliente) => {
                setisModalInativarUsuarioDoCliente(!isModalInativarUsuarioDoCliente);
                setUsuarioClienteSelecionado(item);
              },
            },
            {
              icon: Trash2,
              label: "Excluir usuário",
              textColor: "text-red-600",
              visible: podeInativarUsuario,
              onClick: (item: IUsuarioCliente) => {
                setUsuarioClienteSelecionado(item);
                setIsModalExcluirUsuarioDoCliente(true);
              },
            },
          ]}
        />
      </div>

      {podeEditarUsuario && isModalEditarUsuarioDoCliente && (
        <ModalEditarUsuarioDoCliente onClose={() => setIsModalEditarUsuarioDoCliente(false)} />
      )}

      {podeInativarUsuario && isModalInativarUsuarioDoCliente && (
        <ModalInativarUsuarioDoCliente
          onCancel={() => setisModalInativarUsuarioDoCliente(!isModalInativarUsuarioDoCliente)}
        />
      )}

      {podeInativarUsuario && isModalExcluirUsuarioDoCliente && (
        <ModalExcluirUsuarioDoCliente onCancel={() => setIsModalExcluirUsuarioDoCliente(false)} />
      )}

      {podeCriarUsuario && isModalCadastrarUsuarioDoCliente && (
        <ModalCriarUsuarioCliente
          clienteId={cliente?.id}
          onClose={() => setIsModalCadastrarUsuarioDoCliente(false)}
        />
      )}
    </Tabs>
  );
};

/**
 * EXPORT
 */
export { ManageClientUsers };
